import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Check, X, PackagePlus, ClipboardCheck, Package, Box } from "lucide-react";
import { toast } from "react-toastify";

import "../styles/scansheet.css";
import "../styles/stock-check.css";
import { useTranslation } from "../i18n";
import { usePageTitle } from "../hooks/usePageTitle";
import { logoutProcessor } from "../components/helpers/logoutProcessor";
import {
    playSuccess,
    playError,
    playBoxAdd,
    playComplete,
} from "../components/helpers/audioPlayer";
import HiddenScanInput from "../components/HiddenScanInput";
import FitWord from "../components/FitWord";
import {
    preventScanPageButtonFocus,
    blurClickedScanPageButton,
} from "../components/helpers/scanFocusGuard";
import { apiFetch } from "../api/apiFetch";
import { useReadOnly } from "../access/ReadOnly";

type Phase = "awaiting-location" | "checking";

interface Line {
    sku: string;
    title: string;
    barcode?: string | null;
    expected_qty: number;
    scanned_qty: number;
    /**
     * How much of expected_qty is reserved for an order. Such units ARE on
     * the shelf and are counted like any other — but a pick is not recorded
     * against inventory until dispatch, so an unscanned reserved unit may
     * equally be sitting in a tote. Its absence is therefore never treated as
     * a shortfall, and it is never written off. Greyed to say "accounted
     * for", not "missing".
     */
    allocated_qty?: number;
}

// An inbound pallet parked at the location. Not stock — listed after the
// SKU lines so the counter accounts for it. `seen` flips when its label is
// scanned during this check; unseen pallets are flagged missing on
// completion (never written off).
interface PalletLine {
    pallet_id: string;
    stock_type?: string | null;
    location_since?: string | null;
    missing_at?: string | null;
    seen: boolean;
}

// A pallet crate stored inside the location (2026-10-26): its units are
// stock at this lane, but they are counted by scanning the CRATE, not each
// unit — one line per crate after the SKU rows. Scanning the crate label
// ticks it; an unscanned crate is flagged missing on completion, never
// written off. Scanning a crate label on the Scan-location step opens a
// check of that crate's contents instead (toteId set on the open).
interface CrateLine {
    tote_id: string;
    units: number;
    missing_at?: string | null;
    seen: boolean;
}

// Cap titles so a long product name doesn't blow out the row — show up to
// 40 characters, ellipsing the rest (mirrors the book-in item list).
const capTitle = (s: string) => (s && s.length > 40 ? `${s.slice(0, 40)}…` : s);

const StockCheck = () => {
    usePageTitle("stock_check");
    const navigate = useNavigate();
    const { t } = useTranslation();
    // Access-control read-only handheld mode (ACCESS_CONTROL_PLAN.md §8.4).
    // Scans that would open/count/complete a check are already rejected by
    // HiddenScanInput itself via context; the buttons below are the other
    // way a worker can trigger a mutation (POST /stock-check/*).
    const readOnly = useReadOnly();

    const userData = window.localStorage.getItem("user");
    const userObject =
        userData !== null ? JSON.parse(userData) : { uid: 0, name: "", permissions: "", warehouse: "Unknown" };

    const [phase, setPhase] = useState<Phase>("awaiting-location");
    const [checkId, setCheckId] = useState<number | null>(null);
    const [displayName, setDisplayName] = useState<string>("");
    const [lines, setLines] = useState<Line[]>([]);
    // Inbound pallets recorded at this location (see PalletLine).
    const [pallets, setPallets] = useState<PalletLine[]>([]);
    // Pallet crates stored at this location (see CrateLine). Empty on a
    // crate check.
    const [crates, setCrates] = useState<CrateLine[]>([]);
    // Crate check: the crate id is the display name and the lane it sits in
    // is shown underneath. Null on a location check.
    const [crateLocationName, setCrateLocationName] = useState<string | null>(null);
    const [scanValue, setScanValue] = useState("");
    const [loading, setLoading] = useState(false);
    // Add-to-location confirm (surplus / unexpected-but-known SKU).
    const [addPrompt, setAddPrompt] = useState<{ sku: string; title: string; barcode?: string | null } | null>(null);
    // Complete confirm. "clean" = everything counted; "writeoff" = shortfall exists.
    const [completePrompt, setCompletePrompt] = useState<null | { mode: "clean" | "writeoff" }>(null);
    // Resume confirm — shown when re-opening a location with the operator's
    // own in-progress check still running.
    const [resumePrompt, setResumePrompt] = useState<null | {
        checkId: number; displayName: string; location: string; lines: Line[]; pallets: PalletLine[];
        crates: CrateLine[]; toteId: string | null; locationName: string | null;
    }>(null);
    // Recently-checked confirm — re-opening a location already checked today
    // whose stock hasn't changed since. location is kept so "Stock check
    // again" can re-open it bypassing the guard.
    const [recheckPrompt, setRecheckPrompt] = useState<null | {
        location: string; checkedBy: string; checkedAt: string;
    }>(null);
    // Locked confirm — re-opening a location with someone else's check in
    // progress. "Take over" abandons their check and starts fresh.
    const [takeoverPrompt, setTakeoverPrompt] = useState<null | {
        location: string; checkedBy: string;
    }>(null);
    const [submitting, setSubmitting] = useState(false);

    // Refs so async scan handlers never read stale state.
    const phaseRef = useRef(phase);
    const checkIdRef = useRef(checkId);
    const linesRef = useRef(lines);
    // Barcode of the location under check. A pallet crate's barcode is also
    // a product (the crate) and a tote, so scanning the crate label INSIDE
    // a count of its own contents would book one crate into itself. Such a
    // scan is ignored (Alex, 2026-10-12).
    const openLocationRef = useRef<string | null>(null);
    useEffect(() => { phaseRef.current = phase; }, [phase]);
    useEffect(() => { checkIdRef.current = checkId; }, [checkId]);
    useEffect(() => { linesRef.current = lines; }, [lines]);

    const mountedRef = useRef(true);

    // Leaving the screen deliberately KEEPS the check 'in_progress' so the
    // operator can resume it when they come back. The 30-min server sweep
    // reclaims genuinely-abandoned checks (which also releases the
    // per-location lock). We only abandon explicitly via "Start over".
    useEffect(() => () => {
        mountedRef.current = false;
    }, []);

    // Once every expected unit has been counted (no shortfall), pop the
    // completion dialog automatically so the operator doesn't have to reach
    // for the button. Latches per-completion so dismissing it ("keep
    // scanning") doesn't immediately re-open; resets when the count is no
    // longer complete or the page returns to scan-location.
    const autoCompletedRef = useRef(false);
    useEffect(() => {
        if (phase !== "checking") { autoCompletedRef.current = false; return; }
        const expectedUnits = lines.reduce((a, l) => a + l.expected_qty, 0);
        // A lane of crates with no loose stock is complete once every crate
        // has been scanned, so crates count towards "anything expected".
        const anyExpected = expectedUnits > 0 || crates.length > 0;
        const allCounted = anyExpected && lines.every(l => l.scanned_qty >= l.expected_qty)
            && pallets.every(p => p.seen) && crates.every(c => c.seen);
        if (!allCounted) { autoCompletedRef.current = false; return; }
        if (!autoCompletedRef.current && !completePrompt && !addPrompt && !resumePrompt) {
            autoCompletedRef.current = true;
            setCompletePrompt({ mode: "clean" });
        }
    }, [phase, lines, pallets, crates, completePrompt, addPrompt, resumePrompt]);

    const identity = () => ({
        uid: userObject.uid,
        name: userObject.name,
        warehouse: userObject.warehouse,
    });

    const post = async (path: string, body: Record<string, unknown>) => {
        const res = await apiFetch(path, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...body, ...identity() }),
        });
        if (res.status === 401) {
            logoutProcessor();
            navigate("/login");
            return null;
        }
        return res;
    };

    // Totals for the header counter. matched = units verified against the
    // expectation (capped at expected); surplus = extra units added on top.
    // A pallet crate stored here counts as ONE tick (its units are counted by
    // scanning the crate, never individually — see CrateLine), so a lane of
    // crates with no loose stock reads 0/N rather than 0/0.
    const expectedTotal = lines.reduce((a, l) => a + l.expected_qty, 0) + crates.length;
    const matchedTotal = lines.reduce((a, l) => a + Math.min(l.scanned_qty, l.expected_qty), 0)
        + crates.filter(c => c.seen).length;
    const surplusTotal = lines.reduce((a, l) => a + Math.max(l.scanned_qty - l.expected_qty, 0), 0);

    // ---- Scan handling -------------------------------------------------

    const normalizeLines = (raw: Line[]): Line[] =>
        (raw || []).map((l) => ({
            sku: l.sku, title: l.title, barcode: l.barcode ?? null,
            expected_qty: Number(l.expected_qty), scanned_qty: Number(l.scanned_qty),
            allocated_qty: Number(l.allocated_qty ?? 0),
        }));

    const normalizePallets = (raw: PalletLine[] | undefined): PalletLine[] =>
        (raw || []).map((p) => ({
            pallet_id: p.pallet_id, stock_type: p.stock_type ?? null,
            location_since: p.location_since ?? null, missing_at: p.missing_at ?? null,
            seen: Boolean(p.seen),
        }));

    const normalizeCrates = (raw: CrateLine[] | undefined): CrateLine[] =>
        (raw || []).map((c) => ({
            tote_id: c.tote_id, units: Number(c.units ?? 0),
            missing_at: c.missing_at ?? null, seen: Boolean(c.seen),
        }));

    const enterChecking = (data: any, fallbackLoc: string) => {
        // On a crate check the crate's own label is the "location" scan to
        // ignore mid-count (the crate id is also a product at Pembroke).
        const toteId: string | null = typeof data.toteId === "string" && data.toteId ? data.toteId : null;
        openLocationRef.current = toteId || (typeof data.location === "string" && data.location) || fallbackLoc;
        setCheckId(data.checkId);
        setDisplayName(data.displayName || fallbackLoc);
        setCrateLocationName(toteId ? (data.locationName || data.location || null) : null);
        setLines(normalizeLines(data.lines));
        setPallets(normalizePallets(data.pallets));
        setCrates(normalizeCrates(data.crates));
        setPhase("checking");
    };

    const handleOpen = async (val: string) => {
        // A scan while the "already checked today" prompt is up means the
        // operator is moving on — dismiss it and open whatever they scanned
        // (re-set below if this location is itself a recent check).
        setRecheckPrompt(null);
        const res = await post(`/stock-check/open`, { location: val });
        if (!res) return;
        const data = await res.json().catch(() => null);
        if (!res.ok || !data) {
            playError();
            toast.error(t("server_error"), { toastId: "sc-server" });
            return;
        }
        if (!data.valid) {
            playError();
            if (data.reason === "locked") {
                // Someone else is mid-check here — offer take-over vs. move on.
                setTakeoverPrompt({ location: val, checkedBy: data.checkedBy || "" });
                return;
            }
            const msg =
                data.reason === "wrong_warehouse" ? t("stock_check_wrong_warehouse")
              : data.reason === "not_a_crate"     ? t("stock_check_not_a_crate")
              :                                    t("stock_check_unknown_location");
            toast.error(msg, { toastId: "sc-open" });
            return;
        }
        if (data.resumed) {
            // They've an in-progress check here — let them pick up where they
            // left off, or start over.
            playSuccess();
            setResumePrompt({
                checkId: data.checkId,
                displayName: data.displayName || val,
                location: val,
                lines: normalizeLines(data.lines),
                pallets: normalizePallets(data.pallets),
                crates: normalizeCrates(data.crates),
                toteId: typeof data.toteId === "string" && data.toteId ? data.toteId : null,
                locationName: typeof data.locationName === "string" ? data.locationName : null,
            });
            return;
        }
        if (data.recentlyChecked) {
            // Already checked today, stock unchanged — warn before re-counting.
            playSuccess();
            setRecheckPrompt({ location: val, checkedBy: data.checkedBy || "", checkedAt: data.checkedAt });
            return;
        }
        enterChecking(data, val);
        playSuccess();
    };

    // ---- Recently-checked confirm -------------------------------------

    const recheckAgain = async () => {
        if (!recheckPrompt) return;
        setSubmitting(true);
        try {
            const res = await post(`/stock-check/open`, { location: recheckPrompt.location, confirmRecheck: true });
            const data = res ? await res.json().catch(() => null) : null;
            if (data?.valid) { enterChecking(data, recheckPrompt.location); playSuccess(); }
            else { playError(); toast.error(t("server_error"), { toastId: "sc-server" }); }
        } finally {
            if (mountedRef.current) { setSubmitting(false); setRecheckPrompt(null); }
        }
    };

    const recheckScanNew = () => {
        setRecheckPrompt(null);
        resetToScan();
    };

    const recheckCancel = () => {
        setRecheckPrompt(null);
        handleBack();
    };

    // ---- Locked / take-over confirm -----------------------------------

    const takeOver = async () => {
        if (!takeoverPrompt) return;
        setSubmitting(true);
        try {
            const res = await post(`/stock-check/open`, { location: takeoverPrompt.location, confirmTakeOver: true });
            const data = res ? await res.json().catch(() => null) : null;
            if (data?.valid) { enterChecking(data, takeoverPrompt.location); playSuccess(); }
            else { playError(); toast.error(t("server_error"), { toastId: "sc-server" }); }
        } finally {
            if (mountedRef.current) { setSubmitting(false); setTakeoverPrompt(null); }
        }
    };

    const takeoverScanNew = () => {
        setTakeoverPrompt(null);
        resetToScan();
    };

    const continueResume = () => {
        if (!resumePrompt) return;
        openLocationRef.current = resumePrompt.toteId || resumePrompt.location;
        setCheckId(resumePrompt.checkId);
        setDisplayName(resumePrompt.displayName);
        setCrateLocationName(resumePrompt.toteId ? resumePrompt.locationName : null);
        setLines(resumePrompt.lines);
        setPallets(resumePrompt.pallets);
        setCrates(resumePrompt.crates);
        setPhase("checking");
        setResumePrompt(null);
    };

    const startFresh = async () => {
        if (!resumePrompt) return;
        setSubmitting(true);
        try {
            await post(`/stock-check/cancel`, { checkId: resumePrompt.checkId });
            const res = await post(`/stock-check/open`, { location: resumePrompt.location });
            const data = res ? await res.json().catch(() => null) : null;
            if (data?.valid) enterChecking(data, resumePrompt.location);
            else { playError(); toast.error(t("server_error"), { toastId: "sc-server" }); }
        } finally {
            if (mountedRef.current) { setSubmitting(false); setResumePrompt(null); }
        }
    };

    const handleScan = async (val: string) => {
        const res = await post(`/stock-check/scan`, { checkId: checkIdRef.current, barcode: val });
        if (!res) return;
        const data = await res.json().catch(() => null);
        if (!res.ok || !data) {
            playError();
            toast.error(t("server_error"), { toastId: "sc-server" });
            return;
        }
        switch (data.outcome) {
            case "matched": {
                setLines(prev => prev.map(l =>
                    l.sku === data.sku ? { ...l, scanned_qty: data.scannedForSku } : l));
                playSuccess();
                break;
            }
            case "already_added":
            case "surplus":
            case "known_not_here": {
                // Needs operator confirmation before any stock is booked. The
                // discrepancy (a SKU not expected here / over-count / another
                // unit of a SKU added this session) is flagged with the error
                // sound so the operator notices something's off. already_added
                // used to be a silent acknowledge, which capped a brand-new
                // SKU at 1 unit per session — units 2+ beeped green and were
                // never booked (W25L7, 2026-08-21).
                setAddPrompt({ sku: data.sku, title: data.title, barcode: data.barcode });
                playError();
                break;
            }
            case "not_active": {
                toast.error(t("stock_check_session_ended"), { toastId: "sc-ended" });
                resetToScan();
                break;
            }
            // Inbound pallet labels — verified or re-homed, never booked.
            case "pallet_matched": {
                setPallets(normalizePallets(data.pallets));
                playSuccess();
                break;
            }
            case "pallet_rehomed": {
                setPallets(normalizePallets(data.pallets));
                playBoxAdd();
                toast.info(
                    t("stock_check_pallet_rehomed", {
                        pallet: data.pallet_id,
                        from: data.from_location_name || data.from_location_id || "?",
                    }),
                    { toastId: "sc-pallet-rehomed" },
                );
                break;
            }
            case "pallet_not_placed": {
                playError();
                toast.error(
                    t(data.status === "processed" ? "stock_check_pallet_processed" : "stock_check_pallet_not_placed", { pallet: data.pallet_id }),
                    { toastId: "sc-pallet-np" },
                );
                break;
            }
            case "pallet_wrong_warehouse": {
                playError();
                toast.error(t("pallet_wrong_warehouse"), { toastId: "sc-pallet-wh" });
                break;
            }
            // Pallet crates stored here — ticked by their label, or re-homed
            // (crate AND contents move) when recorded in another lane.
            case "crate_matched": {
                setCrates(normalizeCrates(data.crates));
                playSuccess();
                break;
            }
            case "crate_rehomed": {
                setCrates(normalizeCrates(data.crates));
                playBoxAdd();
                toast.info(
                    t("stock_check_crate_rehomed", {
                        crate: data.tote_id,
                        from: data.from_location_name || data.from_location_id || "?",
                    }),
                    { toastId: "sc-crate-rehomed" },
                );
                break;
            }
            case "crate_wrong_warehouse": {
                playError();
                toast.error(t("stock_check_crate_wrong_warehouse", { crate: data.tote_id }), { toastId: "sc-crate-wh" });
                break;
            }
            case "crate_blocked": {
                setCrates(normalizeCrates(data.crates));
                playError();
                toast.error(t("stock_check_crate_blocked", { crate: data.tote_id }), { toastId: "sc-crate-blocked" });
                break;
            }
            case "crate_other": {
                playError();
                toast.error(t("stock_check_crate_other", { crate: data.tote_id }), { toastId: "sc-crate-other" });
                break;
            }
            default: { // "unknown"
                playError();
                toast.error(t("stock_check_barcode_not_recognised"), { toastId: "sc-unknown" });
            }
        }
    };

    const doSubmit = async (scannedValue?: string) => {
        const val = (scannedValue ?? scanValue).trim();
        if (!val) return;
        setLoading(true);
        try {
            if (phaseRef.current === "awaiting-location") await handleOpen(val);
            else if (openLocationRef.current && val === openLocationRef.current) {
                // The location's own barcode mid-count: not an item here.
                playError();
                toast.info(t("stock_check_location_rescan"), { toastId: "sc-self-scan" });
            }
            else await handleScan(val);
        } catch {
            if (mountedRef.current) {
                playError();
                toast.error(t("server_error"), { toastId: "sc-server" });
            }
        } finally {
            if (mountedRef.current) { setLoading(false); setScanValue(""); }
        }
    };

    // ---- Add to location ----------------------------------------------

    const confirmAdd = async () => {
        if (!addPrompt) return;
        setSubmitting(true);
        try {
            const res = await post(`/stock-check/add`, { checkId: checkIdRef.current, sku: addPrompt.sku, qty: 1 });
            if (!res) return;
            if (res.status === 409) {
                playError();
                toast.error(t("stock_check_session_ended"), { toastId: "sc-ended" });
                resetToScan();
                return;
            }
            if (!res.ok) {
                playError();
                toast.error(t("server_error"), { toastId: "sc-server" });
                return;
            }
            setLines(prev => {
                const found = prev.find(l => l.sku === addPrompt.sku);
                if (found) return prev.map(l => l.sku === addPrompt.sku ? { ...l, scanned_qty: l.scanned_qty + 1 } : l);
                return [...prev, { sku: addPrompt.sku, title: addPrompt.title, barcode: addPrompt.barcode ?? null, expected_qty: 0, scanned_qty: 1 }];
            });
            playBoxAdd();
        } finally {
            if (mountedRef.current) { setSubmitting(false); setAddPrompt(null); }
        }
    };

    // ---- Complete ------------------------------------------------------

    // Lines short of their expected count — but only the part that is NOT
    // reserved. A reserved unit that went unscanned proves nothing: a pick
    // isn't recorded against inventory until dispatch, so it may be on the
    // shelf or already in a tote, and the BE refuses to write it off either
    // way. Offering it here would invite an operator to "write off" stock
    // that is simply mid-journey — and strand the allocation pointing at it.
    const unbackedMissing = (l: Line) =>
        Math.max(0, (l.expected_qty - l.scanned_qty) - Number(l.allocated_qty ?? 0));
    const missingLines = lines.filter(l => unbackedMissing(l) > 0);

    // Render order: outstanding (not yet fully counted) rows stay at the top,
    // completed ones sink to the bottom as they're scanned. Stable sort keeps
    // each group in its original (title) order.
    const isLineDone = (l: Line) => l.scanned_qty > 0 && l.scanned_qty >= l.expected_qty;
    const displayLines = [...lines].sort((a, b) => Number(isLineDone(a)) - Number(isLineDone(b)));

    const openComplete = () => {
        setCompletePrompt({ mode: missingLines.length > 0 ? "writeoff" : "clean" });
    };

    const finishCheck = async (writeOffMissing: boolean) => {
        setSubmitting(true);
        try {
            const res = await post(`/stock-check/complete`, { checkId: checkIdRef.current, writeOffMissing });
            if (!res) return;
            const data = await res.json().catch(() => null);
            if (res.status === 409) {
                // Check was already finished elsewhere or superseded by a newer
                // check on this location — can't complete it again.
                playError();
                toast.error(t("stock_check_session_ended"), { toastId: "sc-ended" });
                resetToScan();
                return;
            }
            if (!res.ok || !data?.success) {
                playError();
                toast.error(t("server_error"), { toastId: "sc-server" });
                return;
            }
            playComplete();
            const s = data.summary || {};
            toast.success(
                t("stock_check_complete_summary", {
                    matched: String(s.matched ?? 0),
                    surplus: String(s.surplus ?? 0),
                    writtenOff: String(s.writtenOff ?? 0),
                }),
                { toastId: "sc-done" },
            );
            if (Number(s.palletsMissing) > 0) {
                toast.warn(
                    t("stock_check_pallets_flagged_missing", { count: String(s.palletsMissing) }),
                    { toastId: "sc-pallets-missing" },
                );
            }
            if (Number(s.cratesMissing) > 0) {
                toast.warn(
                    t("stock_check_crates_flagged_missing", { count: String(s.cratesMissing) }),
                    { toastId: "sc-crates-missing" },
                );
            }
            resetToScan();
        } finally {
            if (mountedRef.current) { setSubmitting(false); setCompletePrompt(null); }
        }
    };

    const resetToScan = () => {
        setCheckId(null);
        setLines([]);
        setPallets([]);
        setCrates([]);
        setCrateLocationName(null);
        setDisplayName("");
        setPhase("awaiting-location");
    };

    const unseenPallets = pallets.filter(p => !p.seen);
    const unseenCrates = crates.filter(c => !c.seen);

    const handleBack = () => {
        navigate("/stock-control");
    };

    const commandWords = t("stock_check_scan_location").toUpperCase().split(/[\s-]+/).filter(Boolean);
    // Blocking modals freeze the scanner. The "already checked today" prompt is
    // deliberately NOT one of them — scanning a new location there should open
    // it straight away (handled in handleOpen), so it stays live.
    const modalOpen = addPrompt !== null || completePrompt !== null || resumePrompt !== null || takeoverPrompt !== null;

    return (
        <div
            className="scan-panel stock-check-panel"
            onMouseDown={preventScanPageButtonFocus}
            onClick={blurClickedScanPageButton}
        >
            <div className="page-title-bar">
                <h1 className="page-title">{t("stock_check")}</h1>
                <button className="page-back-btn" onClick={handleBack}>
                    <ArrowLeft size={20} />
                </button>
            </div>

            {phase === "awaiting-location" && (
                <div className="scan-centre">
                    <div className="scan-command-wrap">
                        <div className="scan-command">
                            {commandWords.map((word, i) => (
                                <FitWord key={i}>{word}</FitWord>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {phase === "checking" && (
                <div className="stock-check-body">
                    <div className="stock-check-header">
                        <div className="stock-check-loc">
                            {crateLocationName ? <Box size={18} /> : <MapPin size={18} />}
                            <strong>{displayName}</strong>
                            {crateLocationName && (
                                <span className="stock-check-loc-sub" data-testid="stock-check-crate-location">
                                    {t("stock_check_crate_in", { location: crateLocationName })}
                                </span>
                            )}
                        </div>
                        <div className="stock-check-counter">
                            <span className="stock-check-counter-num">{matchedTotal}</span>
                            <span className="stock-check-counter-sep">/</span>
                            <span className="stock-check-counter-den">{expectedTotal}</span>
                        </div>
                        {surplusTotal > 0 && (
                            <span className="stock-check-surplus-badge">+{surplusTotal}</span>
                        )}
                    </div>

                    <div className="stock-check-list">
                        {lines.length === 0 && (
                            <div className="stock-check-empty">{t("stock_check_empty_location")}</div>
                        )}
                        {displayLines.map(l => {
                            const added = l.expected_qty === 0;                                  // added-only line
                            const done = l.scanned_qty > 0 && l.scanned_qty >= l.expected_qty;   // green (incl. added)
                            const partial = l.scanned_qty > 0 && l.scanned_qty < l.expected_qty; // orange
                            const surplus = l.expected_qty > 0 && l.scanned_qty > l.expected_qty;
                            // Reserved for an order and not yet counted → grey.
                            // Still fully scannable; the grey only says "we
                            // already know where this is meant to be going".
                            const allocatedQty = Number(l.allocated_qty ?? 0);
                            const allocated = allocatedQty > 0
                                && allocatedQty >= l.expected_qty && l.scanned_qty === 0;
                            return (
                                <div
                                    key={l.sku}
                                    className={`stock-check-row${done ? " stock-check-row--done" : ""}${partial ? " stock-check-row--partial" : ""}${surplus ? " stock-check-row--surplus" : ""}${allocated ? " stock-check-row--allocated" : ""}`}
                                >
                                    <div className="stock-check-row-tick">
                                        {done
                                            ? <Check size={18} />
                                            : <span className={`stock-check-row-dot${partial ? " stock-check-row-dot--partial" : ""}`} />}
                                    </div>
                                    <div className="stock-check-row-main">
                                        <span className="stock-check-row-title">{capTitle(l.title)}</span>
                                        <span className="stock-check-row-identity">
                                            <span className="stock-check-row-sku">{l.sku}</span>
                                            {l.barcode && <span className="stock-check-row-barcode">{l.barcode}</span>}
                                            {added && <span className="stock-check-row-added">· {t("stock_check_added")}</span>}
                                            {allocated && (
                                                <span className="stock-check-row-allocated-tag">
                                                    · {t("stock_check_allocated")}
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <div className="stock-check-row-count">
                                        {l.scanned_qty}<span className="stock-check-row-count-sep">/</span>{l.expected_qty}
                                    </div>
                                </div>
                            );
                        })}
                        {/* Inbound pallets parked here — after the SKU rows.
                            Scanning the label ticks it; an unscanned one is
                            flagged missing on completion, never written off. */}
                        {pallets.map(p => (
                            <div
                                key={p.pallet_id}
                                className={`stock-check-row stock-check-row--pallet${p.seen ? " stock-check-row--done" : ""}`}
                            >
                                <div className="stock-check-row-tick">
                                    {p.seen ? <Check size={18} /> : <span className="stock-check-row-dot" />}
                                </div>
                                <div className="stock-check-row-main">
                                    <span className="stock-check-row-title">
                                        <Package size={14} /> {t("stock_check_pallet")}{p.stock_type ? ` · ${p.stock_type}` : ""}
                                    </span>
                                    <span className="stock-check-row-identity">
                                        <span className="stock-check-row-sku">{p.pallet_id}</span>
                                        {p.missing_at && !p.seen && (
                                            <span className="stock-check-row-added">· {t("stock_check_pallet_was_missing")}</span>
                                        )}
                                    </span>
                                </div>
                                <div className="stock-check-row-count">
                                    {p.seen ? 1 : 0}<span className="stock-check-row-count-sep">/</span>1
                                </div>
                            </div>
                        ))}
                        {/* Pallet crates stored here — one line each, after the
                            SKU rows. Scanning the crate label ticks it (its units
                            are counted by scanning the crate, not each unit); an
                            unscanned crate is flagged missing on completion,
                            never written off. */}
                        {crates.map(c => (
                            <div
                                key={c.tote_id}
                                className={`stock-check-row stock-check-row--crate${c.seen ? " stock-check-row--done" : ""}`}
                            >
                                <div className="stock-check-row-tick">
                                    {c.seen ? <Check size={18} /> : <span className="stock-check-row-dot" />}
                                </div>
                                <div className="stock-check-row-main">
                                    <span className="stock-check-row-title">
                                        <Box size={14} /> {t("stock_check_crate")} · {t("stock_check_crate_units", { count: String(c.units) })}
                                    </span>
                                    <span className="stock-check-row-identity">
                                        <span className="stock-check-row-sku">{c.tote_id}</span>
                                        {c.missing_at && !c.seen && (
                                            <span className="stock-check-row-added">· {t("stock_check_pallet_was_missing")}</span>
                                        )}
                                    </span>
                                </div>
                                <div className="stock-check-row-count">
                                    {c.seen ? 1 : 0}<span className="stock-check-row-count-sep">/</span>1
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Always pressable so the operator can end the check early
                        and write off whatever's missing. NOT gated on `loading`
                        — an in-flight count scan shouldn't block opening the
                        confirm dialog (it would intermittently swallow the tap
                        now that rapid concurrent scanning is allowed). */}
                    <button
                        className="btn btn-primary btn-lg stock-check-complete-btn"
                        onClick={openComplete}
                        disabled={submitting || readOnly}
                        aria-disabled={readOnly}
                        title={readOnly ? t("read_only_mode") : undefined}
                    >
                        <ClipboardCheck size={20} />
                        {t("stock_check_complete")}
                    </button>
                </div>
            )}

            {/* Counting is a rapid-fire workflow, so we must NOT disable the
                scanner during an in-flight matched scan — HiddenScanInput drops
                keystrokes while disabled, which truncates the *next* barcode and
                surfaces a spurious "not recognised". The server increments each
                scan atomically and returns the authoritative count, so
                overlapping count requests are safe. We only block input while a
                confirm modal is open, or briefly during the location-open
                transition (when checkId isn't set yet). */}
            <HiddenScanInput
                value={scanValue}
                onChange={setScanValue}
                onSubmit={doSubmit}
                disabled={modalOpen || submitting || (loading && phase === "awaiting-location")}
            />

            {/* Resume confirm — re-opened a location with own check running */}
            {resumePrompt && (() => {
                // Same arithmetic as the header counter: a stored crate is one tick.
                const counted = resumePrompt.lines.reduce((a, l) => a + Math.min(l.scanned_qty, l.expected_qty), 0)
                    + resumePrompt.crates.filter(c => c.seen).length;
                const total = resumePrompt.lines.reduce((a, l) => a + l.expected_qty, 0) + resumePrompt.crates.length;
                return (
                    <div className="modal-overlay" onClick={() => !submitting && setResumePrompt(null)}>
                        <div className="modal-card stock-check-modal" onClick={e => e.stopPropagation()}>
                            <div className="modal-card-header">
                                <h2>{t("stock_check_resume_title")}</h2>
                                <button className="modal-close" onClick={() => setResumePrompt(null)} disabled={submitting}>
                                    <X size={18} />
                                </button>
                            </div>
                            <div className="modal-card-body">
                                <p className="stock-check-modal-hint">
                                    {t("stock_check_resume_body", {
                                        location: resumePrompt.displayName,
                                        counted: String(counted),
                                        total: String(total),
                                    })}
                                </p>
                                <div className="modal-confirm-buttons">
                                    <button
                                        className="btn btn-ghost btn-lg"
                                        onClick={startFresh}
                                        disabled={submitting || readOnly}
                                        aria-disabled={readOnly}
                                        title={readOnly ? t("read_only_mode") : undefined}
                                    >
                                        {submitting ? t("submitting") : t("stock_check_start_over")}
                                    </button>
                                    <button className="btn btn-primary btn-lg" onClick={continueResume} disabled={submitting}>
                                        {t("stock_check_continue")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Recently-checked confirm — already checked today, stock unchanged */}
            {recheckPrompt && (
                <div className="modal-overlay" onClick={() => !submitting && recheckScanNew()}>
                    <div className="modal-card stock-check-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("stock_check_recheck_title")}</h2>
                            <button className="modal-close" onClick={recheckScanNew} disabled={submitting}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="stock-check-recheck-panel">
                                <div className="stock-check-recheck-cell">
                                    <span className="stock-check-recheck-label">{t("stock_check_recheck_last")}</span>
                                    <span className="stock-check-recheck-time">
                                        {new Date(recheckPrompt.checkedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}
                                    </span>
                                </div>
                                <div className="stock-check-recheck-cell">
                                    <span className="stock-check-recheck-label">{t("stock_check_recheck_by")}</span>
                                    <span className="stock-check-recheck-name">{recheckPrompt.checkedBy}</span>
                                </div>
                            </div>
                            <div className="modal-confirm-buttons modal-confirm-buttons--stacked stock-check-recheck-buttons">
                                <button
                                    className="btn btn-primary btn-lg"
                                    onClick={recheckAgain}
                                    disabled={submitting || readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    {submitting ? t("submitting") : t("stock_check_recheck_again")}
                                </button>
                                <button className="btn btn-ghost btn-lg" onClick={recheckScanNew} disabled={submitting}>
                                    {t("stock_check_recheck_new")}
                                </button>
                                <button className="btn btn-ghost btn-lg" onClick={recheckCancel} disabled={submitting}>
                                    {t("cancel")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Locked confirm — someone else's check is in progress here */}
            {takeoverPrompt && (
                <div className="modal-overlay" onClick={() => !submitting && takeoverScanNew()}>
                    <div className="modal-card stock-check-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("stock_check_locked_title")}</h2>
                            <button className="modal-close" onClick={takeoverScanNew} disabled={submitting}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <p className="stock-check-modal-hint">
                                {t("stock_check_being_checked", { name: takeoverPrompt.checkedBy })}
                            </p>
                            <div className="modal-confirm-buttons modal-confirm-buttons--stacked">
                                <button className="btn btn-ghost btn-lg" onClick={takeoverScanNew} disabled={submitting}>
                                    {t("stock_check_recheck_new")}
                                </button>
                                <button
                                    className="btn btn-primary btn-lg"
                                    onClick={takeOver}
                                    disabled={submitting || readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    {submitting ? t("submitting") : t("stock_check_take_over")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add-to-location confirm */}
            {addPrompt && (
                <div className="modal-overlay" onClick={() => !submitting && setAddPrompt(null)}>
                    <div className="modal-card stock-check-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("stock_check_add_title")}</h2>
                            <button className="modal-close" onClick={() => setAddPrompt(null)} disabled={submitting}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="stock-check-add-card">
                                <PackagePlus size={32} />
                                <div>
                                    <div className="stock-check-add-title">{addPrompt.title}</div>
                                    <div className="stock-check-add-sku">{addPrompt.sku}</div>
                                </div>
                            </div>
                            <p className="stock-check-modal-hint">
                                {t("stock_check_add_body", { location: displayName })}
                            </p>
                            <div className="modal-confirm-buttons">
                                <button className="btn btn-ghost btn-lg" onClick={() => setAddPrompt(null)} disabled={submitting}>
                                    {t("no")}
                                </button>
                                <button
                                    className="btn btn-primary btn-lg"
                                    onClick={confirmAdd}
                                    disabled={submitting || readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    {submitting ? t("submitting") : t("yes")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Complete confirm */}
            {completePrompt && (
                <div className="modal-overlay" onClick={() => !submitting && setCompletePrompt(null)}>
                    <div className="modal-card stock-check-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{completePrompt.mode === "clean" ? t("stock_check_complete") : t("stock_check_writeoff_title")}</h2>
                            <button className="modal-close" onClick={() => setCompletePrompt(null)} disabled={submitting}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            {completePrompt.mode === "clean" ? (
                                <p className="stock-check-modal-hint">{t("stock_check_complete_confirm")}</p>
                            ) : (
                                <>
                                    <p className="stock-check-modal-hint">{t("stock_check_writeoff_body")}</p>
                                    <div className="stock-check-missing-list">
                                        {missingLines.map(l => (
                                            <div key={l.sku} className="stock-check-missing-row">
                                                <span className="stock-check-missing-title">{l.title}</span>
                                                <span className="stock-check-missing-sku">{l.sku}</span>
                                                {/* Only the unreserved shortfall is writable-off, so
                                                    show that figure — not the raw gap, which would
                                                    overstate what this action does. */}
                                                <span className="stock-check-missing-qty">−{unbackedMissing(l)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                            {unseenPallets.length > 0 && (
                                <p className="stock-check-modal-hint stock-check-modal-hint--pallets">
                                    {t("stock_check_pallets_unseen_body", { count: String(unseenPallets.length) })}
                                </p>
                            )}
                            {unseenCrates.length > 0 && (
                                <p className="stock-check-modal-hint stock-check-modal-hint--pallets" data-testid="stock-check-crates-unseen">
                                    {t("stock_check_crates_unseen_body", { count: String(unseenCrates.length) })}
                                </p>
                            )}
                            <div className={`modal-confirm-buttons${completePrompt.mode === "writeoff" ? " modal-confirm-buttons--stacked" : ""}`}>
                                {completePrompt.mode === "clean" ? (
                                    <>
                                        <button className="btn btn-ghost btn-lg" onClick={() => setCompletePrompt(null)} disabled={submitting}>
                                            {t("no")}
                                        </button>
                                        <button
                                            className="btn btn-success btn-lg"
                                            onClick={() => finishCheck(false)}
                                            disabled={submitting || readOnly}
                                            aria-disabled={readOnly}
                                            title={readOnly ? t("read_only_mode") : undefined}
                                        >
                                            {submitting ? t("submitting") : t("yes")}
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button className="btn btn-ghost btn-lg" onClick={() => setCompletePrompt(null)} disabled={submitting}>
                                            {t("stock_check_keep_scanning")}
                                        </button>
                                        <button
                                            className="btn btn-ghost btn-lg"
                                            onClick={() => finishCheck(false)}
                                            disabled={submitting || readOnly}
                                            aria-disabled={readOnly}
                                            title={readOnly ? t("read_only_mode") : undefined}
                                        >
                                            {t("stock_check_complete_no_writeoff")}
                                        </button>
                                        <button
                                            className="btn btn-danger btn-lg"
                                            onClick={() => finishCheck(true)}
                                            disabled={submitting || readOnly}
                                            aria-disabled={readOnly}
                                            title={readOnly ? t("read_only_mode") : undefined}
                                        >
                                            {submitting ? t("submitting") : t("stock_check_writeoff_confirm")}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StockCheck;
